import React, { useMemo, useRef, useState, useEffect } from 'react';
import ProductCard from '../components/ProductCard';
import CustomerInfoModal from '../components/CustomerInfoModal';
import { supabase } from '../supabaseClient';

const CartPage = ({ user, cartItems, onRemoveFromCart, onUpdateQuantity, onFinalizePurchase, onProductClick, onAddToCart, onNavigate }) => {

  const [showCustomerInfoModal, setShowCustomerInfoModal] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);   // 👈 NUEVO
  const [suggestedProducts, setSuggestedProducts] = useState([]);

  const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  const handleFinalizeClick = () => {
    if (cartItems.length === 0) {
      alert('Tu carrito está vacío. Agrega productos antes de finalizar la compra.');
      return;
    }
    if (!user) {
      setShowLoginPrompt(true);            // 👈 si no hay sesión, pide login
      return;
    }
    setShowCustomerInfoModal(true);        // 👈 con sesión, abre el checkout
  };

  const handleConfirmCustomerInfo = (customerInfo) => {
    onFinalizePurchase(customerInfo);
    setShowCustomerInfoModal(false);
  };

  useEffect(() => {
    if (cartItems.length === 0) {
      const fetchSuggestedProducts = async () => {
        const { data, error } = await supabase
          .from('products')
          .select('*')
          .limit(4);

        if (error) {
          console.error('Error al obtener productos sugeridos:', error);
        } else {
          setSuggestedProducts(data);
        }
      };
      fetchSuggestedProducts();
    }
  }, [cartItems]);

  return (
    <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
      <h1 className="text-4xl font-extrabold text-gray-900 mb-8 text-center">Tu Carrito de Compras ({totalItems} artículos)</h1>

      {cartItems.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
          <p className="text-gray-600 text-lg py-8">Tu carrito está vacío. ¡Agrega algunos productos!</p>
          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-6">Productos Sugeridos</h2>
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-4 gap-4 sm:gap-6">
            {suggestedProducts.map((product) => (
              <ProductCard key={product.id} product={product} onAddToCart={onAddToCart} onProductClick={onProductClick} />
            ))}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Columna de Productos en el Carrito */}
          <div className="md:col-span-1 bg-white rounded-2xl shadow-lg p-6">
            <div className="space-y-4">
              {cartItems.map((item) => (
                <div key={item.id} className="flex items-center bg-gray-50 p-4 rounded-lg shadow-sm">
                  <img src={item.image} alt={item.name} className="w-24 h-24 object-cover rounded-md mr-4" />
                  <div className="flex-grow">
                    <h3 className="font-semibold text-lg text-gray-900">{item.name}</h3>
                    <p className="text-gray-700">₡{item.price.toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                    <div className="flex items-center mt-2">
                      <button
                        onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
                        className="bg-gray-200 text-gray-700 px-3 py-1 rounded-l-md hover:bg-gray-300 transition-colors"
                      >
                        -
                      </button>
                      <span className="bg-gray-100 text-gray-800 px-4 py-1">{item.quantity}</span>
                      <button
                        onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
                        className="bg-gray-200 text-gray-700 px-3 py-1 rounded-r-md hover:bg-gray-300 transition-colors"
                      >
                        +
                      </button>
                      <button
                        onClick={() => onRemoveFromCart(item.id)}
                        className="ml-4 text-red-500 hover:text-red-700 transition-colors"
                      >
                        Eliminar
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Columna de Resumen y Botón de Finalizar */}
          <div className="md:col-span-1 flex flex-col bg-white rounded-2xl shadow-lg p-6">
            <div className="bg-gray-50 p-6 rounded-lg shadow-sm mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Resumen del Pedido</h3>
              <div className="flex justify-between text-lg text-gray-800 mb-2">
                <span>Subtotal:</span>
                <span>₡{subtotal.toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between text-lg text-gray-800 font-bold border-t pt-4 mt-4">
                <span>Total:</span>
                <span>₡{subtotal.toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
            </div>

            <button
              onClick={handleFinalizeClick}
              className="w-full bg-[#5A61E5] text-white py-3 rounded-xl hover:bg-[#4A51D5] transition-colors duration-300 shadow-lg text-lg font-semibold"
            >
              Finalizar Compra
            </button>
          </div>
        </div>
      )}

      {/* Modal de checkout (con datos del cliente) */}
      {showCustomerInfoModal && user && (
        <CustomerInfoModal
          userId={user.id}                       // 👈 NUEVO
          onClose={() => setShowCustomerInfoModal(false)}
          onConfirm={handleConfirmCustomerInfo}
        />
      )}

      {/* Modal de “debes iniciar sesión” */}
      {showLoginPrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Inicia sesión</h3>
            <p className="text-gray-700 mb-6">Debes iniciar sesión para finalizar tu compra.</p>
            <div className="flex gap-3">
              <button
  onClick={() => { setShowLoginPrompt(false); onNavigate('/login'); }}
  className="flex-1 bg-[#5A61E5] text-white py-2 rounded-lg hover:bg-[#4A51D5]"
>
  Ir a Login
</button>

              <button
                onClick={() => setShowLoginPrompt(false)}
                className="flex-1 bg-gray-200 text-gray-800 py-2 rounded-lg hover:bg-gray-300"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CartPage;
